import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Menahan elemen tetap terpasang selama animasi KELUAR berjalan.
//
// CSS tak bisa menganimasikan sesuatu yang sudah dilepas dari DOM: begitu React
// melepasnya, simpulnya hilang seketika dan tak ada yang tersisa untuk
// dianimasikan. Hook ini menunda pelepasan itu selama `ms`, dan selama jeda
// itu `closing` bernilai true supaya pemakainya bisa memasang kelas keluar.
//
// Dibuat sendiri, bukan memakai pustaka animasi: yang dibutuhkan hanya ini,
// dan menambah dependensi runtime di aplikasi yang deploy otomatis tiap push
// berarti menambah sesuatu yang bisa gagal di produksi demi hal yang tak
// menyentuh data.
export function usePresence(open, ms = 180) {
  const [terpasang, setTerpasang] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setTerpasang(true);
      setClosing(false);
      return undefined;
    }
    // Sudah lepas — tak ada yang perlu dianimasikan keluar.
    let batal = false;
    setClosing((sedang) => sedang || true);
    const t = setTimeout(() => {
      if (batal) return;
      setTerpasang(false);
      setClosing(false);
    }, ms);
    return () => {
      batal = true;
      clearTimeout(t);
    };
  }, [open, ms]);

  return { terpasang: open || terpasang, closing: !open && closing };
}

// Menyorot sekejap saat NILAINYA berganti — bukan saat pertama dirender.
// Memuat ulang data yang isinya sama persis tidak menyalakan apa pun.
export function useFlashOnChange(value) {
  const [flash, setFlash] = useState(false);
  const sebelumnya = useRef(value);

  useEffect(() => {
    if (sebelumnya.current === value) return undefined;
    sebelumnya.current = value;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 700);
    return () => clearTimeout(t);
  }, [value]);

  return flash;
}

export function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function Icon({ name, className = "" }) {
  return (
    <span className={cx("material-symbols-outlined", className)}>
      {name}
    </span>
  );
}

export function Spinner({ label = "Loading..." }) {
  return (
    <div className="anim-fade flex flex-col items-center justify-center gap-2 py-10 text-slate-500">
      <Icon name="progress_activity" className="animate-spin text-4xl text-primary" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function Empty({ label }) {
  return (
    <div className="anim-fade rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500 dark:border-slate-700">
      {label}
    </div>
  );
}

export function Button({
  children,
  className = "",
  variant = "primary",
  ...props
}) {
  const styles =
    variant === "danger"
      ? "bg-red-600 hover:bg-red-500 text-white"
      : variant === "secondary"
      ? "bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700"
      : variant === "success"
      ? "bg-green-600 hover:bg-green-500 text-white"
      : "bg-primary hover:bg-primary/90 text-white button-glow";

  return (
    <button
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100",
        styles,
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

// Base memakai w-full HANYA bila pemanggil tak menentukan lebar (mis. w-28/w-40),
// supaya lebar yang dikirim tak tertimpa w-full (yang menang di urutan CSS Tailwind).
export function Input({ className = "", ...props }) {
  const hasWidth = /(^|\s)(sm:|md:|lg:)?w-/.test(className);
  return (
    <input
      {...props}
      className={cx(
        !hasWidth && "w-full",
        "rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white",
        className
      )}
    />
  );
}

export function Select({ children, className = "", ...props }) {
  const hasWidth = /(^|\s)(sm:|md:|lg:)?w-/.test(className);
  return (
    <select
      {...props}
      className={cx(
        !hasWidth && "w-full",
        "rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white",
        className
      )}
    >
      {children}
    </select>
  );
}

export function Toast({ toast, setToast }) {
  const { terpasang, closing } = usePresence(!!toast, 200);

  // Isi terakhir disimpan supaya selama animasi TURUN, teksnya masih ada.
  // Tanpa ini toast akan berubah jadi kotak kosong tepat saat ia pergi.
  const terakhir = useRef(toast);
  useEffect(() => {
    if (toast) terakhir.current = toast;
  }, [toast]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast, setToast]);

  const isi = toast || terakhir.current;
  if (!terpasang || !isi) return null;

  return (
    <div
      className={cx(
        // -translate-x-1/2 ikut diurus keyframe kipToast/kipToastOut; kalau
        // ditulis juga sebagai kelas Tailwind, ia menimpa transform animasi
        // dan toast-nya melompat ke kiri.
        "fixed bottom-8 left-1/2 z-[9999] rounded-xl px-5 py-3 text-sm font-bold text-white shadow-xl",
        closing ? "anim-toast-out" : "anim-toast",
        isi.type === "success" ? "bg-green-600" : "bg-red-600"
      )}
    >
      {isi.message}
    </div>
  );
}

// Dialog dipasang lewat PORTAL ke <body>, bukan di tempat ia ditulis.
//
// `position: fixed` diukur terhadap layar HANYA bila tak ada satu pun leluhur
// yang punya `transform`, `filter`, atau `perspective`. Leluhur seperti itu
// berubah jadi "containing block", dan `inset-0` milik dialog mendadak diukur
// terhadap kotak leluhur itu — bukan layar.
//
// Itu benar-benar terjadi: pembungkus animasi per-halaman menganimasikan
// `transform`, dan dialog jadi terpusat di tengah DOKUMEN yang tinggi. Di layar
// ia tampak nyangkut di dasar halaman dan harus digulir dulu.
//
// Portal menaruhnya sebagai anak <body>, jadi tak ada leluhur yang bisa
// mengubah acuannya — sekarang maupun kalau ada yang menambah transform di
// tempat lain besok. Ini memang cara yang benar untuk dialog, terlepas dari bug
// yang memunculkannya.
// Latar gelap dialog — satu-satunya tempat markup ini boleh hidup.
//
// Sebelumnya markup yang sama disalin di empat berkas, dan ketika `position:
// fixed`-nya bermasalah, tiga salinan lain diam-diam ikut bermasalah tanpa ada
// yang tahu. Sekarang siapa pun yang butuh latar dialog memakai ini, dan
// perbaikan di sini berlaku untuk semuanya.
export function ModalOverlay({ children, onClose, closing = false }) {
  return createPortal(
    <div
      className={cx(
        "fixed inset-0 z-[999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm",
        closing ? "anim-fade-out" : "anim-fade"
      )}
      onMouseDown={(e) => onClose && e.target === e.currentTarget && onClose()}
    >
      {children}
    </div>,
    document.body
  );
}

export function Modal({ title, open, onClose, children, wide = false }) {
  // `open` jadi false tak langsung melepas dialognya — ia ditahan 180ms dulu
  // supaya sempat mengecil dan memudar. Lihat catatan di usePresence.
  const { terpasang, closing } = usePresence(open, 180);
  if (!terpasang) return null;

  return (
    <ModalOverlay onClose={onClose} closing={closing}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx(
          "neon-border max-h-[92vh] overflow-auto rounded-2xl border bg-white p-6 shadow-2xl dark:bg-slate-900",
          closing ? "anim-pop-out" : "anim-pop",
          wide ? "w-full max-w-4xl" : "w-full max-w-xl"
        )}
      >
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className="text-2xl font-black neon-text-glow">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <Icon name="close" />
          </button>
        </div>

        {children}
      </div>
    </ModalOverlay>
  );
}

export function PageTitle({ title, subtitle, action }) {
  return (
    <div className="anim-rise mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
      <div>
        <h1 className="text-3xl font-black tracking-tight md:text-4xl">
          {title}
        </h1>
        <p className="mt-2 text-slate-500">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

// `delay` 0-4: menunda munculnya kartu ini, supaya halaman berisi beberapa
// kartu terbaca dari atas ke bawah alih-alih meledak sekaligus. Pemakainya yang
// menentukan urutannya — bukan kebetulan urutan DOM.
export function DataCard({ children, className = "", delay = 0, ...props }) {
  return (
    <div
      {...props}
      className={cx(
        "anim-rise overflow-auto rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-primary/10 dark:bg-slate-900/30",
        delay > 0 && `anim-d${Math.min(4, Math.round(delay))}`,
        className
      )}
    >
      {children}
    </div>
  );
}

export function Th({
  children,
  right = false,
  center = false,
  sticky = false,
  stickyRight = false
}) {
  return (
    <th
      className={cx(
        "bg-slate-100 px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-500 dark:bg-slate-800 dark:text-slate-400",
        right && "text-right",
        center && "text-center",
        sticky && "sticky left-0 z-20",
        stickyRight && "sticky right-0 z-20"
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  right = false,
  center = false,
  mono = false,
  primary = false,
  sticky = false,
  stickyRight = false,
  className = "",
  ...props
}) {
  return (
    <td
      {...props}
      className={cx(
        "px-4 py-3 text-sm text-slate-600 dark:text-slate-300",
        right && "text-right",
        center && "text-center",
        mono && "font-mono",
        primary && "text-primary",
        sticky && "sticky left-0 z-10 bg-white dark:bg-slate-900",
        stickyRight && "sticky right-0 z-10 bg-white dark:bg-slate-900",
        className
      )}
    >
      {children}
    </td>
  );
}

export function Badge({ children }) {
  return (
    <span className="inline-flex rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-black text-primary">
      {children}
    </span>
  );
}

export function AvatarName({ name }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary font-black text-white">
        {String(name || "?")[0].toUpperCase()}
      </div>
      <p className="font-black text-slate-900 dark:text-white">{name}</p>
    </div>
  );
}

export function Stat({ label, value, good, bad, primary }) {
  // Angka ringkasan sering berubah setelah pengguna menekan sesuatu — dan
  // perubahan itulah yang justru paling mudah terlewat.
  const flash = useFlashOnChange(value);

  return (
    <div className="anim-fade rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/50">
      <p className="text-xs font-bold uppercase text-slate-500">{label}</p>
      <p
        className={cx(
          "mt-2 text-xl font-black",
          flash && "anim-flash",
          good && "text-green-500",
          bad && "text-red-500",
          primary && "text-primary"
        )}
      >
        {value}
      </p>
    </div>
  );
}